import { ReactNode, useState, VFC } from 'react'
import ReactSlick, { Settings as ReactSlickSettings } from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { postNotInterested } from 'src/api/recommendation'
import { useLoginUser } from 'src/api/user'
import { LeftCaretIcon, RightCaretIcon } from 'src/assets/svgs'
import {
  RequestReasonModal,
  ShowReasonModalMeta,
} from 'src/components/RequestReasonModal'
import { getActions } from 'src/components/UserCard'
import { PrDetailCardModal } from 'src/components/UserCard/PrDetailCard'
import { PromotionParam } from 'src/models/Promotion'
import { shouldDisplayPaidPlanLabel } from 'src/models/User/role'
import { black } from 'src/styles/colors'
import { companyDetail } from 'src/utils/routes'
import styled from 'styled-components'
import { SalesDemoModal } from '../Messages/Modals/SalesDemoModal'
import { Section } from './Section'

export type UserCardFC<T extends { id: number; promotion?: PromotionParam }> =
  VFC<{
    user: T
    showDetail: (id: number) => void
    showReasonModal: (
      postInterested: ShowReasonModalMeta['postInterested'],
    ) => unknown
    closeReasonModal: () => unknown
    displayPrLabel?: boolean
    showPrDetailModal?: () => void
    displayCertified?: boolean
    free?: boolean
    openSalesDemoModal: () => void
    salesDemoPlan: boolean
  }>

type UserCardsProps<T extends { id: number; promotion?: PromotionParam }> = {
  title: string
  description?: ReactNode
  users: T[]
  CardComponent: UserCardFC<T>
  displayPrLabel?: boolean
}

type UserCardsPropsFC = <T extends { id: number; promotion?: PromotionParam }>(
  props: UserCardsProps<T>,
) => JSX.Element

export const UserCards: UserCardsPropsFC = ({
  title,
  description,
  users,
  CardComponent,
  displayPrLabel,
}) => {
  const [showReasonModalMeta, setShowReasonModalMeta] = useState<
    ShowReasonModalMeta | undefined
  >()
  const [showPrDetailModal, setShowPrDetailModal] = useState(false)
  const [showSalesDemoModal, setShowSalesDemoModal] = useState<boolean>(false)
  const promotionUser = users.find((user) => user.promotion)
  const { user: loginUser } = useLoginUser()

  return (
    <>
      <UserCardsSection title={title} description={description}>
        <ReactSlick
          arrows={users.length >= 2}
          slidesToShow={Math.min(3, users.length)}
          {...settings}
        >
          {users.map((user) => (
            <SlickItem key={user.id}>
              <CardComponent
                user={user}
                showDetail={(id: number) =>
                  window.open(companyDetail(id, false, false), '_blank')
                }
                showReasonModal={(
                  postInterested: ShowReasonModalMeta['postInterested'],
                ) =>
                  setShowReasonModalMeta({
                    postInterested,
                    openSalesDemoModal: () => setShowSalesDemoModal(true),
                  })
                }
                closeReasonModal={() => setShowReasonModalMeta(undefined)}
                displayPrLabel={displayPrLabel}
                showPrDetailModal={() => setShowPrDetailModal(true)}
                displayCertified={shouldDisplayPaidPlanLabel([
                  loginUser && loginUser.plan.role,
                ])}
                free={loginUser?.plan.free}
                openSalesDemoModal={() => setShowSalesDemoModal(true)}
                salesDemoPlan={loginUser?.plan.salesDemo || false}
              />
            </SlickItem>
          ))}
        </ReactSlick>
      </UserCardsSection>
      {showReasonModalMeta && (
        <RequestReasonModal
          {...showReasonModalMeta}
          close={() => setShowReasonModalMeta(undefined)}
        />
      )}
      {promotionUser && promotionUser.promotion && showPrDetailModal && (
        <PrDetailCardModal
          prDetail={promotionUser.promotion.prDetail}
          userId={6}
          close={() => setShowPrDetailModal(false)}
          actions={getActions(
            promotionUser,
            () => postNotInterested(promotionUser.id, promotionUser.promotion),
            (postInterested: ShowReasonModalMeta['postInterested']) => {
              setShowReasonModalMeta({ postInterested })
              setShowPrDetailModal(false)
            },
          )}
        />
      )}
      {showSalesDemoModal && (
        <SalesDemoModal close={() => setShowSalesDemoModal(false)} />
      )}
    </>
  )
}

const settingsWide: ReactSlickSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToScroll: 1,
  centerMode: true,
  centerPadding: '5%',
  nextArrow: <RightCaretIcon fill={black} />,
  prevArrow: <LeftCaretIcon fill={black} />,
}
const settingsMidddle: ReactSlickSettings = {
  slidesToShow: 1,
  centerPadding: '35%',
  arrows: false,
}
const settingsNarrow: ReactSlickSettings = {
  slidesToShow: 1,
  centerMode: false,
  arrows: false,
}
const settings: ReactSlickSettings = {
  ...settingsWide,
  responsive: [
    {
      breakpoint: 1500,
      settings: settingsMidddle,
    },
    {
      breakpoint: 1350,
      settings: settingsNarrow,
    },
  ],
}

const UserCardsSection = styled(Section)`
  .slick-slide {
    > div {
      display: flex;
    }
  }
  .slick-arrow {
    transform: translate(-50%, -50%);
    font-size: 3em;
    z-index: 1;
    color: inherit;
    :hover {
      opacity: 0.5;
      color: inherit;
    }
    width: 1.5em;
    height: 1.5em;
    &.slick-prev {
      left: 35%;
    }
    &.slick-next {
      left: 65%;
    }
  }
`

const SlickItem = styled.div`
  margin: 0 auto;
  max-width: 432.2px;
  min-width: 432.2px;
`
